// Mark Payment Complete Dialog
//
// For vouchers in the "Awaiting Payment" (status=READY) bucket whose payment
// was settled outside the Payment Hub. Captures a settlement date, an optional
// reference number, and requires a proof-of-payment file upload. On submit,
// uploads the file and PATCHes the voucher to status=REDEEMED with
// completionMethod=MANUAL. "Paid by" is read-only and reflects the
// logged-in user (also stamped server-side via activity.actor_id).

import { ChangeEvent, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
	Alert,
	alpha,
	Avatar,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaidIcon from '@mui/icons-material/Paid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';

import { ContextBanner, ContextUser } from '../../../App.context';
import { uploadAiDocument } from '../../../services/aiApi';
import { radius } from '../../../utils/designSystem';
import { BlockchainProcessing } from '../../ui/BlockchainProcessing/BlockchainProcessing';

// Minimal shape needed for the dialog. Accepts either a raw `Voucher`
// (which uses `amount` and has no `name`/`supplierName`) or the legacy
// `VoucherDetails` display type used by the Payments page.
export interface MarkPaymentVoucher {
	id: number;
	currency?: string;
	name?: string;
	supplierName?: string;
	paymentAmount?: number;
	amount?: number;
	// Optional: the planned FX rates captured at prepare time. When present,
	// the dialog snapshots them as settlement-time rates with the actual
	// settlement date (`dateProcessed`). Per-currency rate override UX is a
	// follow-up; today the rate carries from plan and only the date moves.
	exchangeRates?: { currency: string; exchangeRate: number; dateExchangeRate: string }[] | null;
	// Invoice already recorded for this milestone (supplier-provided). When
	// present it prefills the invoice fields so manual completion echoes the
	// recorded invoice instead of blank/today placeholders.
	invoicePrefill?: {
		num: string | null;
		date: string | null;
		amount: number | null;
		currency: string | null;
	} | null;
}

export interface DialogMarkPaymentCompleteProps {
	voucher: MarkPaymentVoucher | null;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	projectId?: number;
}

const today = () => new Date().toISOString().slice(0, 10);

export const DialogMarkPaymentComplete = ({
	voucher,
	isOpen,
	onClose,
	onSuccess,
	projectId,
}: DialogMarkPaymentCompleteProps) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { user } = useContext(ContextUser);
	const { setBanner } = useContext(ContextBanner);

	const [dateProcessed, setDateProcessed] = useState<string>(today());
	const [referenceNum, setReferenceNum] = useState<string>('');
	// Invoice fields the v1 Record Payment form captures. All four are required
	// at manual completion so the v1 mirror writer doesn't fall back to
	// placeholder values.
	const [invoiceNumber, setInvoiceNumber] = useState<string>('');
	const [invoiceDate, setInvoiceDate] = useState<string>(today());
	const [invoiceAmount, setInvoiceAmount] = useState<string>('');
	const [invoiceCurrency, setInvoiceCurrency] = useState<string>('');
	const [file, setFile] = useState<File | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Reset form whenever the dialog (re)opens for a voucher
	useEffect(() => {
		if (isOpen) {
			setDateProcessed(today());
			setReferenceNum('');
			// Pre-fill invoice fields from the recorded invoice when one exists,
			// falling back to the voucher amount/currency; user can edit if needed.
			const inv = voucher?.invoicePrefill;
			setInvoiceNumber(inv?.num || '');
			// slice() (not new Date()) to avoid a timezone day-shift on ISO datetimes
			setInvoiceDate(inv?.date ? inv.date.slice(0, 10) : today());
			const voucherAmount = voucher?.amount ?? voucher?.paymentAmount ?? null;
			const prefillAmount = inv?.amount ?? voucherAmount;
			setInvoiceAmount(prefillAmount != null ? String(prefillAmount) : '');
			setInvoiceCurrency(inv?.currency || voucher?.currency || '');
			setFile(null);
			setError(null);
			setIsSubmitting(false);
		}
	}, [
		isOpen,
		voucher?.id,
		voucher?.amount,
		voucher?.paymentAmount,
		voucher?.currency,
		// scalar fields, not the object — callers rebuild invoicePrefill inline
		// each render and an identity dep would wipe user edits mid-dialog
		voucher?.invoicePrefill?.num,
		voucher?.invoicePrefill?.date,
		voucher?.invoicePrefill?.amount,
		voucher?.invoicePrefill?.currency,
	]);

	const paidByDisplay = user
		? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email
		: '';

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0] || null;
		setFile(f);
		e.target.value = '';
	};

	const invoiceAmountNum = Number(invoiceAmount);
	const invoiceFieldsValid =
		!!invoiceNumber.trim() &&
		!!invoiceDate &&
		invoiceAmount.trim() !== '' &&
		Number.isFinite(invoiceAmountNum) &&
		invoiceAmountNum > 0 &&
		!!invoiceCurrency.trim();

	const canSubmit = !!voucher && !!dateProcessed && !!file && invoiceFieldsValid && !isSubmitting;

	const handleSubmit = async () => {
		if (!voucher || !file || !dateProcessed) return;
		setIsSubmitting(true);
		setError(null);

		try {
			// 1. Upload proof of payment
			await uploadAiDocument({
				file,
				doc_type: 'payment_proof',
				project_id: projectId,
				source_type: 'voucher',
				source_id: voucher.id,
				tag: `voucher:${voucher.id}`,
				date: dateProcessed,
			});

			// 2. PATCH voucher to REDEEMED with manual completion details
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');
			if (!accessToken || !idToken) {
				throw new Error('Not authenticated');
			}
			const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/v1\/?$/, '');
			// Settlement-time FX snapshot. The rate carries from the planned
			// exchange_rates captured at prepare time; only the date moves to the
			// actual settlement date. Per-currency rate override UX is a follow-up.
			const plannedRates = voucher.exchangeRates || [];
			const settlementExchangeRates = plannedRates.length
				? plannedRates.map((r) => ({
						currency: r.currency,
						exchangeRate: r.exchangeRate,
						dateExchangeRate: dateProcessed,
					}))
				: null;

			const res = await fetch(`${apiUrl}/v2/vouchers/${voucher.id}`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					status: 'REDEEMED',
					completionMethod: 'MANUAL',
					// Backend requires a full ISO 8601 datetime; the date picker
					// only gives us YYYY-MM-DD, so anchor it to start-of-day UTC.
					dateProcessed: new Date(`${dateProcessed}T00:00:00Z`).toISOString(),
					invoiceNumber: invoiceNumber.trim(),
					invoiceDate,
					invoiceAmount: invoiceAmountNum,
					invoiceCurrency: invoiceCurrency.trim(),
					...(settlementExchangeRates ? { settlementExchangeRates } : {}),
					...(referenceNum.trim() ? { referenceNum: referenceNum.trim() } : {}),
				}),
			});

			if (!res.ok) {
				const errBody = await res.json().catch(() => ({}));
				throw new Error(errBody?.error?.message || `Request failed (${res.status})`);
			}

			const okBody = await res.json().catch(() => ({}));
			const txHash: string | null = okBody?.data?.redemptionTransactionHash ?? null;

			setBanner({
				message: txHash
					? t(
							'markPaymentComplete.successWithTx',
							'Payment marked as complete. On-chain redemption: {{txHash}}',
							{ txHash }
						)
					: t('markPaymentComplete.success', 'Payment marked as complete.'),
				severity: 'success',
			});
			onSuccess?.();
			onClose();
		} catch (err) {
			const msg =
				err instanceof Error
					? err.message
					: t('markPaymentComplete.failed', 'Failed to mark payment complete.');
			setError(msg);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog
			open={isOpen}
			onClose={isSubmitting ? undefined : onClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{ sx: { borderRadius: radius.lg, maxWidth: 560 } }}>
			{/* Header */}
			<Box
				sx={{
					px: 3,
					py: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar
						sx={{
							bgcolor: alpha(theme.palette.success.main, 0.12),
							width: 36,
							height: 36,
						}}>
						<PaidIcon sx={{ color: 'success.main', fontSize: 20 }} />
					</Avatar>
					<Box>
						<Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
							{t('markPaymentComplete.title', 'Mark Payment Complete')}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t('markPaymentComplete.subtitle', 'Manual confirmation')}
						</Typography>
					</Box>
				</Stack>
				<IconButton onClick={onClose} disabled={isSubmitting} size="small">
					<CloseIcon />
				</IconButton>
			</Box>

			<DialogContent sx={{ px: 3, py: 2.5 }}>
				{isSubmitting ? (
					<BlockchainProcessing status="LOADING" />
				) : (
					<Stack spacing={2}>
						<Alert
							severity="info"
							variant="outlined"
							sx={{ borderRadius: radius.md, alignItems: 'flex-start' }}>
							{t(
								'markPaymentComplete.info',
								'Use this only when the payment was settled outside the Payment Hub (e.g. wire transfer, manual disbursement). Attach proof of payment to complete the record.'
							)}
						</Alert>

						<TextField
							label={t('markPaymentComplete.paidBy', 'Paid by')}
							value={paidByDisplay}
							InputProps={{ readOnly: true }}
							fullWidth
							size="small"
							helperText={t('markPaymentComplete.paidByHint', 'You — captured automatically')}
						/>

						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
							<TextField
								label={t('markPaymentComplete.settlementDate', 'Settlement date')}
								type="date"
								value={dateProcessed}
								onChange={(e) => setDateProcessed(e.target.value)}
								InputLabelProps={{ shrink: true }}
								required
								fullWidth
								size="small"
							/>
							<TextField
								label={t('markPaymentComplete.paymentReference', 'Payment reference')}
								value={referenceNum}
								onChange={(e) => setReferenceNum(e.target.value)}
								placeholder={t(
									'markPaymentComplete.paymentReferencePlaceholder',
									'Wire confirmation # (optional)'
								)}
								fullWidth
								size="small"
							/>
						</Stack>

						{/* Invoice details. The four fields the v1 Record Payment form
					    captures, asked here so the user doesn't have to bounce back into
					    v1 to fill them in after manual completion. */}
						<Box>
							<Typography
								variant="caption"
								sx={{
									display: 'block',
									mb: 1,
									color: 'text.secondary',
									fontWeight: 600,
									textTransform: 'uppercase',
									letterSpacing: 0.4,
								}}>
								{t('markPaymentComplete.invoice', 'Invoice')}
							</Typography>
							<Stack spacing={2}>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<TextField
										label={t('markPaymentComplete.invoiceNumber', 'Invoice number')}
										value={invoiceNumber}
										onChange={(e) => setInvoiceNumber(e.target.value)}
										required
										fullWidth
										size="small"
									/>
									<TextField
										label={t('markPaymentComplete.invoiceDate', 'Invoice date')}
										type="date"
										value={invoiceDate}
										onChange={(e) => setInvoiceDate(e.target.value)}
										InputLabelProps={{ shrink: true }}
										required
										fullWidth
										size="small"
									/>
								</Stack>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<TextField
										label={t('markPaymentComplete.invoiceAmount', 'Invoice amount')}
										type="number"
										value={invoiceAmount}
										onChange={(e) => setInvoiceAmount(e.target.value)}
										inputProps={{ min: 0, step: 'any' }}
										required
										fullWidth
										size="small"
										error={
											invoiceAmount.trim() !== '' &&
											(!Number.isFinite(invoiceAmountNum) || invoiceAmountNum <= 0)
										}
										helperText={
											invoiceAmount.trim() !== '' &&
											(!Number.isFinite(invoiceAmountNum) || invoiceAmountNum <= 0)
												? t('markPaymentComplete.positiveAmount', 'Enter a positive amount.')
												: undefined
										}
									/>
									<TextField
										label={t('markPaymentComplete.invoiceCurrency', 'Invoice currency')}
										value={invoiceCurrency}
										onChange={(e) => setInvoiceCurrency(e.target.value.toUpperCase())}
										placeholder={t('markPaymentComplete.currencyPlaceholder', 'e.g. USD')}
										inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
										required
										fullWidth
										size="small"
									/>
								</Stack>
							</Stack>
						</Box>

						{/* Proof of payment */}
						<Box>
							<Typography
								variant="caption"
								sx={{
									display: 'block',
									mb: 1,
									color: 'text.secondary',
									fontWeight: 600,
									textTransform: 'uppercase',
									letterSpacing: 0.4,
								}}>
								{t('markPaymentComplete.proofOfPayment', 'Proof of payment')}{' '}
								<span style={{ color: theme.palette.error.main }}>*</span>
							</Typography>
							<Box
								sx={{
									border: `1px dashed ${
										file ? theme.palette.success.main : alpha(theme.palette.primary.main, 0.4)
									}`,
									borderRadius: radius.md,
									bgcolor: file
										? alpha(theme.palette.success.main, 0.04)
										: alpha(theme.palette.primary.main, 0.03),
									px: 2,
									py: 1.5,
									display: 'flex',
									alignItems: 'center',
									gap: 1.5,
									transition: 'background-color 120ms, border-color 120ms',
									'&:hover': !file
										? {
												bgcolor: alpha(theme.palette.primary.main, 0.06),
												borderColor: theme.palette.primary.main,
											}
										: undefined,
								}}>
								{file ? (
									<>
										<Avatar
											variant="rounded"
											sx={{
												bgcolor: alpha(theme.palette.success.main, 0.12),
												color: 'success.main',
												width: 36,
												height: 36,
											}}>
											<DescriptionIcon fontSize="small" />
										</Avatar>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography
												variant="body2"
												sx={{
													fontWeight: 600,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}>
												{file.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{Math.ceil(file.size / 1024)} KB
											</Typography>
										</Box>
										<Button
											component="label"
											size="small"
											variant="text"
											disabled={isSubmitting}
											sx={{ textTransform: 'none', fontWeight: 600 }}>
											{t('markPaymentComplete.replace', 'Replace')}
											<input
												type="file"
												hidden
												onChange={handleFileChange}
												accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff"
											/>
										</Button>
									</>
								) : (
									<>
										<Avatar
											variant="rounded"
											sx={{
												bgcolor: alpha(theme.palette.primary.main, 0.1),
												color: 'primary.main',
												width: 36,
												height: 36,
											}}>
											<UploadFileIcon fontSize="small" />
										</Avatar>
										<Box sx={{ flex: 1 }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{t('markPaymentComplete.uploadFile', 'Upload a file')}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{t(
													'markPaymentComplete.uploadHint',
													'PDF or image — wire receipt, bank statement, etc.'
												)}
											</Typography>
										</Box>
										<Button
											component="label"
											size="small"
											variant="outlined"
											disabled={isSubmitting}
											sx={{
												textTransform: 'none',
												fontWeight: 600,
												borderRadius: radius.md,
											}}>
											{t('markPaymentComplete.browse', 'Browse')}
											<input
												type="file"
												hidden
												onChange={handleFileChange}
												accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff"
											/>
										</Button>
									</>
								)}
							</Box>
						</Box>

						{error && (
							<Alert severity="error" sx={{ borderRadius: radius.md }}>
								{error}
							</Alert>
						)}
					</Stack>
				)}
			</DialogContent>

			{!isSubmitting && (
				<DialogActions
					sx={{
						px: 3,
						py: 2,
						borderTop: `1px solid ${theme.palette.divider}`,
					}}>
					<Button
						onClick={onClose}
						disabled={isSubmitting}
						sx={{ textTransform: 'none', fontWeight: 600 }}>
						{t('cancel')}
					</Button>
					<Button
						variant="contained"
						color="success"
						onClick={handleSubmit}
						disabled={!canSubmit}
						sx={{ textTransform: 'none', fontWeight: 600, borderRadius: radius.md }}>
						{isSubmitting
							? t('markPaymentComplete.submitting', 'Submitting…')
							: t('markPaymentComplete.markPaid', 'Mark Paid')}
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
};

export default DialogMarkPaymentComplete;
